import { useState, useEffect } from "react";
import { useWebsite } from "../../../context/WebsiteContext";
import { useCTF } from "../../../context/CTFContext";

import { WebInput, WebButton } from "./shared";

import CursorEffects from "../../UI/CursorEffects";

// challenges tab component
function ChallengesTab({
  challenges,
  loading,
  error,
  lastResult,
  submitting,
  selectedChallenge,
  selectedChallengeId,
  setSelectedChallengeId,
  flagInput,
  setFlagInput,
  submitFlag,
  clearError,
  handleKeyDown,
}: any) {
  const groupedChallenges = challenges.reduce((acc: Record<string, any[]>, c: any) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {} as Record<string, any[]>);

  const submitFlagHandler = () => {
    if (!flagInput.trim() || !selectedChallenge || submitting) return;
    submitFlag(selectedChallenge.id, flagInput);
    setFlagInput("");
  };

  return (
    <>
      {/* left sidebar - challenge list */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0px",
          minWidth: "170px",
          borderRight: "2px solid #808080",
          minHeight: 0,
        }}
      >
        {/* per-category lists */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            padding: "4px",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {Object.keys(groupedChallenges).length === 0 ? (
            <span style={{ fontSize: "10px", color: "#666666", padding: "0px 8px" }}>
              No challenges
            </span>
          ) : (
            Object.entries(groupedChallenges).map(([category, items]) => (
              <div
                key={category}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0px",
                    backgroundColor: "#c0c0c0",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ fontSize: "11px", fontWeight: "bold" }}>{category}</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                  {(items as any[]).map((challenge: any) => (
                    <div
                      key={challenge.id}
                      onClick={() => setSelectedChallengeId(challenge.id)}
                      style={{
                        padding: "6px",
                        backgroundColor: (() => {
                          if (selectedChallenge?.id === challenge.id) {
                            return !challenge.isCompleted ? "#000080" : "#008000";
                          }
                          return !challenge.isCompleted ? "#e0e0e0" : "#00f00036";
                        })(),
                        color:
                          selectedChallenge?.id === challenge.id ? "#ffffff" : "#000000",
                        cursor: "pointer",
                        borderRadius: "2px",
                        fontSize: "11px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "normal",
                        border: "1px solid #999999",
                      }}
                    >
                      <span
                        style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {challenge.title}
                      </span>

                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "8px", flexShrink: 0 }}>
                        <span style={{ fontSize: "10px", color: "#333333", background: "#f3f3f3", padding: "2px 6px", borderRadius: "3px", border: "1px solid #ccc" }}>
                          {challenge.score} pts
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {loading && (
          <span style={{ fontSize: "10px", color: "#666666", padding: "0px 8px" }}>
            Loading...
          </span>
        )}
      </div>

      {/* right side - challenge details and submission */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0px",
          flex: 1,
          backgroundColor: "#c0c0c0",
          minHeight: 0,
        }}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            padding: "8px",
            backgroundColor: "#c0c0c0",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: "11px", fontWeight: "bold" }}>Details</span>
        </div>

        {/* content */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            flex: 1,
            padding: "8px",
            overflowY: "auto",
            backgroundColor: "#c0c0c0",
            minHeight: 0,
          }}
        >
          {selectedChallenge ? (
            <>
              <div
                style={{
                  padding: "8px",
                  border: "2px solid #808080",
                  backgroundColor: "#ffffff",
                  boxShadow:
                    "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
                  borderRadius: "2px",
                }}
              >
                <div>
                  <span style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selectedChallenge.title}
                  </span>
                  <span
                    style={{ fontSize: "10px", color: "#666666", marginLeft: "8px" }}
                  >
                    [{selectedChallenge.category}]
                  </span>
                </div>

                <div style={{ marginTop: "6px", fontSize: "11px", color: "#333333", fontWeight: "bold" }}>
                  Score: {selectedChallenge.score} pts
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    color: "#333333",
                    whiteSpace: "pre-wrap",
                    marginTop: "6px",
                  }}
                >
                  {selectedChallenge.description}
                </div>

                {selectedChallenge.maxAttempts !== null &&
                  selectedChallenge.isCompleted === false && (
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#666666",
                        marginTop: "6px",
                      }}
                    >
                      Attempts: {selectedChallenge.attemptsUsed}/
                      {selectedChallenge.maxAttempts}
                    </div>
                  )}
              </div>

              {selectedChallenge.isCompleted ? (
                <div
                  style={{
                    padding: "8px",
                    border: "2px solid #808080",
                    backgroundColor: "#e8f5e9",
                    color: "#008000",
                    fontSize: "11px",
                    fontWeight: "bold",
                    boxShadow:
                      "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
                    borderRadius: "2px",
                  }}
                >
                  ✓ Challenge Completed!
                </div>
              ) : (
                <div
                  style={{
                    padding: "8px",
                    border: "2px solid #808080",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    boxShadow:
                      "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
                    borderRadius: "2px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <WebInput
                      style={{
                        flex: 1,
                        height: "26px",
                        minWidth: "0",
                        padding: "4px",
                        border: "1px inset #dfdfdf",
                        fontFamily: "inherit",
                      }}
                      value={flagInput}
                      onChange={(e) => setFlagInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="DOM{enter_the_full_flag_here}"
                    />
                    <WebButton onClick={submitFlagHandler} label={"Submit"} />
                  </div>

                  {lastResult?.challengeId === selectedChallenge.id && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: lastResult.isCorrect ? "#008000" : "#cc0000",
                        padding: "8px",
                        backgroundColor: lastResult.isCorrect
                          ? "#e8f5e9"
                          : "#ffebee",
                        border: `2px solid ${lastResult.isCorrect ? "#4caf50" : "#f44336"}`,
                        boxShadow:
                          "inset 1px 1px 0px #ffffff, inset -1px -1px 0px #666666",
                        borderRadius: "2px",
                      }}
                    >
                      {lastResult.message}
                      {lastResult.attemptsRemaining !== null &&
                        !lastResult.isCorrect && (
                          <span> ({lastResult.attemptsRemaining} left)</span>
                        )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: "8px", color: "#666666" }}>
              Select a challenge from the list
            </div>
          )}

          {error && (
            <div
              style={{
                fontSize: "11px",
                color: "#cc0000",
                padding: "8px",
                backgroundColor: "#ffebee",
                border: "2px solid #f44336",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: "inset 1px 1px 0px #ffffff, inset -1px -1px 0px #666666",
                borderRadius: "2px",
                marginTop: "auto",
              }}
            >
              <span>{error}</span>
              <button
                onClick={() => clearError()}
                style={{
                  background: "none",
                  border: "none",
                  color: "#cc0000",
                  cursor: "pointer",
                  fontSize: "12px",
                  padding: "0 4px",
                }}
              >
                ✕
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// scoreboard tab component
function ScoreboardTab({ scoreboard, scoreboardLoading, settings }: any) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", padding: "6px" }}>

      {(!settings.displayName || !settings.isOptedIn) && (
        <div style={{ fontSize: "12px", color: "#666666", fontStyle: "italic", textAlign: "center" }}>
          {!settings.displayName && "Set a username in Config to appear on the scoreboard"}
          {settings.displayName && !settings.isOptedIn && "Enable scoreboard display in Config to appear on the leaderboard"}
        </div>
      )}

      {scoreboardLoading ? (
        <span style={{ color: "#666666", textAlign: "center" }}>Loading scoreboard...</span>
      ) : scoreboard.length === 0 ? (
        <span style={{ color: "#666666", textAlign: "center" }}>No scores yet</span>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              gap: "12px",
              padding: "8px",
              backgroundColor: "#808080",
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: "bold",
              border: "2px solid #808080",
              boxShadow: "inset 1px 1px 0px #dfdfdf",
              borderRadius: "2px",
            }}
          >
            <div>Rank</div>
            <div>User</div>
            <div style={{ textAlign: "right" }}>Score</div>
          </div>
          {scoreboard.map((entry, index) => (
            <div
              key={`${entry.name}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: "12px",
                padding: "8px",
                backgroundColor: index % 2 === 0 ? "#ffffff" : "#f0f0f0",
                border: "2px solid #808080",
                fontSize: "11px",
                boxShadow: "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
                borderRadius: "2px",
              }}
            >
              <div style={{ fontWeight: "bold" }}>{index + 1}</div>
              <div
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {entry.name}
              </div>
              <div style={{ textAlign: "right", fontWeight: "bold" }}>
                {entry.score}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// about tab component
function AboutTab({
  settings,
  usernameInput,
  setUsernameInput,
  updateSettings,
  error,
  clearError,
}: any) {

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        padding: "12px",
        overflow: "auto",
      }}
    >
      {/* username section */}
      <div
        style={{
          padding: "12px",
          border: "2px solid #808080",
          backgroundColor: "#ffffff",
          boxShadow:
            "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
          borderRadius: "2px",
        }}
      >
        <div style={{ fontSize: "11px", fontWeight: "bold", marginBottom: "8px" }}>
          Scoreboard Name
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <input
            type="text"
            value={usernameInput}
            onChange={(e) => setUsernameInput(e.target.value)}
            placeholder="Enter username for scoreboard"
            style={{
              padding: "4px 6px",
              fontSize: "10px",
              border: "2px solid #808080",
              backgroundColor: "#ffffff",
              boxShadow: "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
              borderRadius: "2px",
              fontFamily: "inherit",
            }}
          />
          <button
            onClick={() => {
              updateSettings(usernameInput || undefined, undefined);
            }}
            style={{
              padding: "4px 12px",
              fontSize: "10px",
              backgroundColor: "#c0c0c0",
              border: "2px outset #dfdfdf",
              cursor: "pointer",
              userSelect: "none",
              fontWeight: "bold",
              borderRadius: "2px",
            }}
          >
            Save
          </button>
          {settings.displayName && (
            <div style={{ fontSize: "9px", color: "#008000" }}>
              ✓ Currently showing as: <strong>{settings.displayName}</strong>
            </div>
          )}
        </div>
      </div>

      {/* opt-in section */}
      <div
        style={{
          padding: "12px",
          border: "2px solid #808080",
          backgroundColor: "#ffffff",
          boxShadow:
            "inset 1px 1px 0px #dfdfdf, inset -1px -1px 0px #808080",
          borderRadius: "2px",
        }}
      >
        <div style={{ fontSize: "11px", fontWeight: "bold", marginBottom: "8px" }}>
          Scoreboard Settings
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <input
            type="checkbox"
            checked={settings.isOptedIn}
            onChange={(e) => updateSettings(undefined, e.target.checked)}
            disabled={!settings.displayName}
            style={{ cursor: settings.displayName ? "pointer" : "not-allowed", width: "16px", height: "16px" }}
          />
          <label style={{ fontSize: "10px", cursor: settings.displayName ? "pointer" : "not-allowed" }}>
            Show my score on the scoreboard
          </label>
        </div>
        {!settings.displayName && (
          <div style={{ fontSize: "9px", color: "#666666", fontStyle: "italic", marginTop: "8px" }}>
            Set a username above to enable scoreboard display
          </div>
        )}
        {settings.isOptedIn && settings.displayName && (
          <div style={{ fontSize: "9px", color: "#008000", marginTop: "8px" }}>
            ✓ Your score is now visible on the scoreboard
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            fontSize: "11px",
            color: "#cc0000",
            padding: "8px",
            backgroundColor: "#ffebee",
            border: "2px solid #f44336",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "inset 1px 1px 0px #ffffff, inset -1px -1px 0px #666666",
            borderRadius: "2px",
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => clearError()}
            style={{
              background: "none",
              border: "none",
              color: "#cc0000",
              cursor: "pointer",
              fontSize: "12px",
              padding: "0 4px",
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

export default function Default() {
  const { currentURL, setPageTitle, setPageIcon } = useWebsite();
  const {
    challenges,
    scoreboard,
    fetchScoreboard,
    loading,
    scoreboardLoading,
    error,
    lastResult,
    submitting,
    submitFlag,
    updateSettings,
    clearError,
    userScore,
    settings,
  } = useCTF();

  useEffect(() => {
    setPageTitle("Dom :: CTF");
    setPageIcon(undefined);
  }, [currentURL, setPageTitle, setPageIcon]);

  const [selectedTab, setSelectedTab] = useState<"challenges" | "scoreboard" | "about">("challenges");
  const [flagInput, setFlagInput] = useState("");
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);
  const [usernameInput, setUsernameInput] = useState(settings.displayName || "");

  const selectedChallenge =
    challenges.find((c) => c.id === selectedChallengeId) || challenges[0];

  const submitFlagHandler = () => {
    if (!flagInput.trim() || !selectedChallenge || submitting) return;
    submitFlag(selectedChallenge.id, flagInput);
    setFlagInput("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !submitting) {
      // prevent default and stop propagation so Enter only triggers when input focused
      e.preventDefault();
      e.stopPropagation();
      submitFlagHandler();
    }
  };

  useEffect(() => {
    if (selectedTab === "scoreboard") {
      fetchScoreboard();
    }
  }, [selectedTab, fetchScoreboard]);

  const completedCount = challenges.filter((c) => c.isCompleted).length;
  const totalCount = challenges.length;

  return (
    <CursorEffects emoji={["🏆", "🎉", "💻", "🕵️‍♂️", "🔐"]} delay={50}>
      <div
        style={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          width: "100%",
          maxWidth: "560px",
          height: "100%",
          padding: "12px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* <img src="./img_era/8301.gif" alt="Dom CTF Logo" style={{ marginBottom: "12px" }} /> */}

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "2px",
              width: "100%",
              backgroundColor: "#c0c0c0",
              padding: "6px 6px 0 6px",
              borderRadius: "6px 6px 0 0",
              borderBottom: "2px solid #808080",
            }}
          >
            <div
              onClick={() => setSelectedTab("challenges")}
              style={{
                padding: "6px 14px",
                backgroundColor: selectedTab === "challenges" ? "#808080" : "#c0c0c0",
                color: selectedTab === "challenges" ? "#ffffff" : "#000000",
                cursor: "pointer",
                border:
                  selectedTab === "challenges" ? "2px inset #808080" : "2px outset #dfdfdf",
                fontSize: "11px",
                fontWeight: selectedTab === "challenges" ? "bold" : "normal",
                borderRadius: "2px 2px 0 0",
                userSelect: "none",
              }}
            >
              Challenges
            </div>
            <div
              onClick={() => setSelectedTab("scoreboard")}
              style={{
                padding: "6px 14px",
                backgroundColor: selectedTab === "scoreboard" ? "#808080" : "#c0c0c0",
                color: selectedTab === "scoreboard" ? "#ffffff" : "#000000",
                cursor: "pointer",
                border:
                  selectedTab === "scoreboard" ? "2px inset #808080" : "2px outset #dfdfdf",
                fontSize: "11px",
                fontWeight: selectedTab === "scoreboard" ? "bold" : "normal",
                borderRadius: "2px 2px 0 0",
                userSelect: "none",
              }}
            >
              Scoreboard
            </div>
            <div
              onClick={() => setSelectedTab("about")}
              style={{
                padding: "6px 14px",
                backgroundColor: selectedTab === "about" ? "#808080" : "#c0c0c0",
                color: selectedTab === "about" ? "#ffffff" : "#000000",
                cursor: "pointer",
                border:
                  selectedTab === "about" ? "2px inset #808080" : "2px outset #dfdfdf",
                fontSize: "11px",
                fontWeight: selectedTab === "about" ? "bold" : "normal",
                borderRadius: "2px 2px 0 0",
                userSelect: "none",
              }}
            >
              Config
            </div>
            <div style={{ flex: 1 }} />
            {selectedTab === "challenges" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "8px",
                  fontSize: "10px",
                  color: "#000000",
                  fontWeight: "bold",
                  gap: "8px",
                }}
              >
                <div>
                  {completedCount}/{totalCount} completed
                </div>
                &middot;
                <div style={{ fontSize: "10px", color: "#000000", fontWeight: "bold" }}>
                  Score: {userScore}
                </div>
              </div>
            )}
          </div>

          {/* content */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              justifyContent: "flex-start",
              gap: "0px",
              width: "100%",
              backgroundColor: "#c0c0c0",
              minHeight: 0,
              padding: "4px",
              paddingBottom: "8px",
              borderRadius: "0 0 6px 6px",
            }}
          >
            {selectedTab === "challenges" && (
              <ChallengesTab
                challenges={challenges}
                loading={loading}
                error={error}
                lastResult={lastResult}
                submitting={submitting}
                selectedChallenge={selectedChallenge}
                selectedChallengeId={selectedChallengeId}
                setSelectedChallengeId={setSelectedChallengeId}
                flagInput={flagInput}
                setFlagInput={setFlagInput}
                submitFlag={submitFlag}
                clearError={clearError}
                handleKeyDown={handleKeyDown}
              />
            )}

            {selectedTab === "scoreboard" && (
              <ScoreboardTab
                scoreboard={scoreboard}
                scoreboardLoading={scoreboardLoading}
                settings={settings}
              />
            )}

            {selectedTab === "about" && (
              <AboutTab
                settings={settings}
                usernameInput={usernameInput}
                setUsernameInput={setUsernameInput}
                updateSettings={updateSettings}
                error={error}
                clearError={clearError}
              />
            )}
          </div>
        </div>
      </div>
    </CursorEffects>
  );
}
